import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar, Clock, Users, Settings, Plus, Trash2, LogOut, Edit, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

// ============================
// AVAILABILITY TAB
// ============================
const AvailabilityTab = () => {
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [newSlots, setNewSlots] = useState([{ start: '', end: '' }]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/availability`, getAuth());
      setDates(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addDate = async () => {
    if (!newDate) return;
    const validSlots = newSlots.filter(s => s.start && s.end);
    if (validSlots.length === 0) return toast.error('Add at least one time slot');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/availability`, {
        date: newDate,
        is_open: true,
        slots: validSlots.map(s => ({ start_time: s.start, end_time: s.end }))
      }, getAuth());
      setNewDate('');
      setNewSlots([{ start: '', end: '' }]);
      load();
      toast.success('Date added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const deleteDate = async (id) => {
    if (!window.confirm('Delete this date?')) return;
    try {
      await axios.delete(`${API}/admin/availability/${id}`, getAuth());
      load();
      toast.success('Deleted');
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Add New */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-blue-500" /> Add Available Date
        </h3>
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-slate-600 mb-1 block">Date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              data-testid="add-date-input" className="max-w-xs rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-sm text-slate-600 mb-2 block">Time Slots</Label>
            {newSlots.map((slot, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input type="time" value={slot.start} onChange={(e) => {
                  const s = [...newSlots]; s[i].start = e.target.value; setNewSlots(s);
                }} className="rounded-xl border-slate-200" />
                <span className="text-slate-400">to</span>
                <Input type="time" value={slot.end} onChange={(e) => {
                  const s = [...newSlots]; s[i].end = e.target.value; setNewSlots(s);
                }} className="rounded-xl border-slate-200" />
                {newSlots.length > 1 && (
                  <button onClick={() => setNewSlots(newSlots.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setNewSlots([...newSlots, { start: '', end: '' }])}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-1">
              <Plus className="w-3 h-3" /> Add slot
            </button>
          </div>
          <Button onClick={addDate} disabled={loading} data-testid="add-date-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
            {loading ? 'Adding...' : 'Add Date'}
          </Button>
        </div>
      </div>

      {/* Existing Dates */}
      <div className="space-y-3">
        {dates.length === 0 ? (
          <p className="text-center text-slate-400 py-8">No dates added yet</p>
        ) : dates.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-slate-800">{d.date}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${d.is_open ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {d.is_open ? 'Open' : 'Closed'}
                </span>
              </div>
              <button onClick={() => deleteDate(d.id)} className="text-red-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {d.slots?.map((slot) => (
                <span key={slot.id} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${
                  slot.status === 'available' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'
                }`}>
                  <Clock className="w-3 h-3" />
                  {slot.start_time} - {slot.end_time}
                  <span className="opacity-60">({slot.status})</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================
// QUESTIONS TAB
// ============================
const QuestionsTab = () => {
  const [questions, setQuestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ question: '', question_type: 'text', options: '', required: true, order: 0 });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/questions`, getAuth());
      setQuestions(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addQuestion = async () => {
    if (!form.question) return;
    try {
      const opts = form.options ? form.options.split(',').map(o => o.trim()).filter(Boolean) : [];
      await axios.post(`${API}/admin/questions`, {
        question: form.question,
        question_type: form.question_type,
        options: opts,
        required: form.required,
        order: form.order
      }, getAuth());
      setForm({ question: '', question_type: 'text', options: '', required: true, order: 0 });
      setShowForm(false);
      load();
      toast.success('Question added');
    } catch (e) { toast.error('Failed'); }
  };

  const deleteQ = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/admin/questions/${id}`, getAuth());
      load();
      toast.success('Deleted');
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Funnel Questions</h3>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Add Question
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div>
            <Label className="text-sm text-slate-600 mb-1 block">Question</Label>
            <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
              data-testid="question-text" placeholder="What is your company name?" className="rounded-xl border-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Type</Label>
              <Select value={form.question_type} onValueChange={(v) => setForm({ ...form, question_type: v })}>
                <SelectTrigger data-testid="question-type" className="rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="textarea">Textarea</SelectItem>
                  <SelectItem value="select">Dropdown</SelectItem>
                  <SelectItem value="radio">Multi-Select (Checkbox)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="rounded-xl border-slate-200" />
            </div>
          </div>
          {(form.question_type === 'select' || form.question_type === 'radio') && (
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Options (comma separated)</Label>
              <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })}
                placeholder="Option 1, Option 2, Option 3" className="rounded-xl border-slate-200" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
            <Label className="text-sm text-slate-600">Required</Label>
          </div>
          <div className="flex gap-2">
            <Button onClick={addQuestion} data-testid="save-question-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">Save</Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {questions.length === 0 ? (
          <p className="text-center text-slate-400 py-8">No questions yet. Users will skip this step.</p>
        ) : questions.map((q) => (
          <div key={q.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between">
            <div>
              <p className="font-medium text-slate-800">{q.question} {q.required && <span className="text-red-500">*</span>}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{q.question_type}</span>
                <span className="text-xs text-slate-400">Order: {q.order}</span>
                {q.options?.length > 0 && <span className="text-xs text-slate-400">Options: {q.options.join(', ')}</span>}
              </div>
            </div>
            <button onClick={() => deleteQ(q.id)} className="text-red-400 hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================
// BOOKINGS TAB
// ============================
const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [dateFilter, setDateFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, getAuth());
      setBookings(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = dateFilter
    ? bookings.filter(b => b.date === dateFilter)
    : bookings;

  const cancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await axios.put(`${API}/admin/bookings/${id}/status?status=cancelled`, {}, getAuth());
      load();
      toast.success('Booking cancelled');
    } catch (e) { toast.error('Failed'); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, getAuth());
      load();
      toast.success('Deleted');
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-slate-800">
          All Bookings ({filtered.length}{dateFilter ? ` of ${bookings.length}` : ''})
        </h3>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
            data-testid="booking-date-filter" className="rounded-xl border-slate-200 w-44" />
          {dateFilter && (
            <button onClick={() => setDateFilter('')} className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table view */}
      {filtered.length === 0 ? (
        <p className="text-center text-slate-400 py-8">{dateFilter ? 'No bookings for this date' : 'No bookings yet'}</p>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email / Phone</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Time</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Answers</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4 font-medium text-slate-800 whitespace-nowrap">{b.user_name}</td>
                    <td className="px-5 py-4 text-slate-600">
                      <div>{b.user_email}</div>
                      {b.user_phone && <div className="text-xs text-slate-400">{b.user_phone}</div>}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">{b.date}</span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-slate-600">{b.start_time} - {b.end_time}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        b.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>{b.status}</span>
                    </td>
                    <td className="px-5 py-4">
                      {Object.keys(b.questionnaire_answers || {}).length > 0 ? (
                        <details>
                          <summary className="text-xs text-blue-600 cursor-pointer">View</summary>
                          <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                            {Object.entries(b.questionnaire_answers).map(([k, v]) => (
                              <p key={k}><span className="font-medium">{k}:</span> {v}</p>
                            ))}
                          </div>
                        </details>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {b.status === 'confirmed' && (
                          <button onClick={() => cancelBooking(b.id)} className="text-orange-500 hover:text-orange-600" title="Cancel">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => deleteBooking(b.id)} className="text-red-400 hover:text-red-500" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {filtered.map((b) => (
              <div key={b.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="font-semibold text-slate-800">{b.user_name}</p>
                    <p className="text-xs text-slate-500">{b.user_email}{b.user_phone && ` | ${b.user_phone}`}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">{b.date}</span>
                      <span className="text-xs bg-slate-50 text-slate-700 px-2 py-1 rounded-lg">{b.start_time} - {b.end_time}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        b.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>{b.status}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {b.status === 'confirmed' && (
                      <button onClick={() => cancelBooking(b.id)} className="text-orange-500 hover:text-orange-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => deleteBooking(b.id)} className="text-red-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================
// SETTINGS TAB
// ============================
const SettingsTab = () => {
  const [settings, setSettings] = useState({ reminder_hours: 24, admin_timezone: 'Asia/Dhaka', time_format: '12' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, getAuth());
        setSettings(res.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuth());
      toast.success('Settings saved');
    } catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <h3 className="font-semibold text-slate-800">Booking Settings</h3>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div>
          <Label className="text-sm text-slate-600 mb-1.5 block">Reminder Hours Before Meeting</Label>
          <Input type="number" step="0.5" value={settings.reminder_hours || ''}
            data-testid="reminder-hours"
            onChange={(e) => setSettings({ ...settings, reminder_hours: parseFloat(e.target.value) || 0 })}
            className="max-w-xs rounded-xl border-slate-200" />
          <p className="text-xs text-slate-400 mt-1">Set to 0 to disable reminders</p>
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1.5 block">Admin Timezone</Label>
          <Select value={settings.admin_timezone || 'Asia/Dhaka'} onValueChange={(v) => setSettings({ ...settings, admin_timezone: v })}>
            <SelectTrigger data-testid="admin-timezone" className="max-w-xs rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Asia/Dhaka">Asia/Dhaka (GMT+6)</SelectItem>
              <SelectItem value="Asia/Kolkata">Asia/Kolkata (GMT+5:30)</SelectItem>
              <SelectItem value="UTC">UTC</SelectItem>
              <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
              <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
              <SelectItem value="Asia/Dubai">Asia/Dubai (GMT+4)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1.5 block">Time Format</Label>
          <Select value={settings.time_format || '12'} onValueChange={(v) => setSettings({ ...settings, time_format: v })}>
            <SelectTrigger data-testid="time-format" className="max-w-xs rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12 Hour (AM/PM)</SelectItem>
              <SelectItem value="24">24 Hour</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={loading} data-testid="save-settings-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// ============================
// MAIN ADMIN DASHBOARD
// ============================
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('availability');
  const adminName = localStorage.getItem('admin_name') || 'Admin';

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/ma');
    }
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
  };

  const tabs = [
    { id: 'availability', label: 'Availability', icon: Calendar },
    { id: 'questions', label: 'Questions', icon: Edit },
    { id: 'bookings', label: 'Bookings', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">Booking Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:inline">Hi, {adminName}</span>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-lg">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'availability' && <AvailabilityTab />}
        {activeTab === 'questions' && <QuestionsTab />}
        {activeTab === 'bookings' && <BookingsTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
